import "../JSONTools/JSONformatter.css";
import ToolHeader from "../../Components/ToolHeader";
import { ToolModeToggle } from "../../Components/ToolActionButtons";
import { Row, Col } from "react-bootstrap";
import { useState, useEffect, useMemo, useRef } from "react";
import { Crongenerate, Cronparse } from "../../Services/APIService";

import { ReactComponent as CopyIcon } from "../../Icons/Copy.svg";
import { ReactComponent as CaretDownIcon } from "../../Icons/CaretDown.svg";
import cronstrue from "cronstrue";
import "./Cronparser.css";

const frequencyMap = [
    { frequency: "Every minute" },
    { frequency: "Hourly" },
    { frequency: "Daily" },
    { frequency: "Weekly" },
    { frequency: "Monthly" }
];

const frequencyEnumMap = {
    "Every minute": "EVERY_MINUTE",
    "Hourly": "HOURLY",
    "Daily": "DAILY",
    "Weekly": "WEEKLY",
    "Monthly": "MONTHLY",
};

const FIELD_INFO = [
    { label: "minute", range: "(0-59)", min: 0, max: 59 },
    { label: "hour", range: "(0-23)", min: 0, max: 23 },
    { label: "day of month", range: "(1-31)", min: 1, max: 31 },
    { label: "month", range: "(1-12)", min: 1, max: 12 },
    { label: "day of week", range: "(0-6 Sun-Sat)", min: 0, max: 6 },
];

const REFERENCE_BY_FIELD = {
    0: [
        ["* * * * *", "Every minute"],
        ["*/5 * * * *", "Every 5 minutes"],
        ["*/15 * * * *", "Every 15 minutes"],
    ],
    1: [
        ["0 * * * *", "Every hour"],
        ["0 */2 * * *", "Every 2 hours"],
        ["0 9-17 * * *", "9am–5pm"],
    ],
    2: [
        ["0 0 1 * *", "First day of month"],
        ["0 0 15 * *", "15th of month"],
    ],
    3: [
        ["0 0 1 1 *", "Jan 1st"],
        ["0 0 1 */3 *", "Quarterly"],
    ],
    4: [
        ["0 9 * * 1-5", "Weekdays 9am"],
        ["0 0 * * 0", "Every Sunday"],
    ],
};

const ALL_REFERENCE = [
    ["* * * * *", "Every minute"],
    ["*/5 * * * *", "Every 5 minutes"],
    ["0 * * * *", "Every hour"],
    ["0 9 * * *", "Every day at 9am"],
    ["0 9 * * 1-5", "Weekdays at 9am"],
];

const parserTimezone = "UTC";
const parserOccurrences = 5;

function clampNumber(value, min, max) {
    if (value === "" || value === null || value === undefined) return "";
    const n = Number(value);
    if (Number.isNaN(n)) return min;
    return Math.min(Math.max(n, min), max);
}

function normalizeCronFieldValue(value, field) {
    if (/^-?\d+$/.test(value)) {
        return String(clampNumber(value, field.min, field.max));
    }

    return value;
}

function CronGeneratorParser() {
    const [mode, setMode] = useState("Generator");
    const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
    const [frequency, setFrequency] = useState("Daily");
    const [hasCopied, setHasCopied] = useState(false)
    const [cron, setCron] = useState({
        minute: 0,
        hour: 9,
        day: 1,
        weekday: 1,
    });
    const [cronExpression, setCronExpression] = useState("");
    const [values, setValues] = useState([  "*/5","*","*","*","1-5",]);
    const [activeIndex, setActiveIndex] = useState(null);
    const inputRefs = useRef([]);
    const [parserCronExpression, setParserCronExpression] = useState("");
    const [parserResponse, setParserResponse] = useState({
        valid: true,
        description: "",
        nextExecutions: [],
        timezone: "UTC",
        processingTimeMs: 0,
        cronExpression: "",
    });
    const toolFav = {toolName: "Cron Parser & Generator",Iconpath: "/hash.svg"};

    

    

    // GENERATE CRON
    useEffect(() => {
        const generateCronExpression = async () => {
            try {
                const payload = {
                    frequency: frequencyEnumMap[frequency],
                    hour: Number(cron.hour),
                    minute: Number(cron.minute),
                };
                if (frequency === "Weekly") {
                    payload.dayOfWeek = Number(cron.weekday);
                }
                if (frequency === "Monthly") {
                    payload.dayOfMonth = Number(cron.day);
                }
                const response = await Crongenerate(payload);
                setCronExpression(response?.data || "");
            } catch (error) {
                console.error("Cron generation failed:", error);
                setCronExpression(
                    buildCron(
                        frequency,
                        cron
                    )
                );
            }
        };
        generateCronExpression();
    }, [frequency, cron]);

    // PARSER
    const expr = useMemo(() => {
        return values.map(v => v || "*").join(" ");
    }, [values]);

    useEffect(() => {
        setParserCronExpression(expr);

        const parseCron = async () => {
            if (!expr.trim()) {
                setParserResponse((prev) => ({
                    ...prev,
                    valid: false,
                    description: "Enter a cron expression to parse.",
                    nextExecutions: [],
                    processingTimeMs: 0,
                    cronExpression: expr,
                    timezone: parserTimezone || "UTC",
                }));
                return;
            }

            try {
                const payload = {
                    cronExpression: expr,
                    timezone: parserTimezone || "UTC",
                    occurrences: Number(parserOccurrences) || 0,
                };

                const response = await Cronparse(payload);
                const data = response?.data || {};

                setParserResponse({
                    valid: data.valid ?? false,
                    description: data.description || "",
                    nextExecutions: data.nextExecutions || [],
                    timezone: data.timezone || parserTimezone || "UTC",
                    processingTimeMs: data.processingTimeMs ?? 0,
                    cronExpression: data.cronExpression || expr,
                });
            } catch (error) {
                console.error("Cron parse failed:", error);
                let fallbackDescription = "Invalid cron expression";
                try {
                    fallbackDescription = cronstrue.toString(expr);
                } catch (_) {}
                setParserResponse({
                    valid: false,
                    description: fallbackDescription,
                    nextExecutions: [],
                    timezone: parserTimezone || "UTC",
                    processingTimeMs: 0,
                    cronExpression: expr,
                });
            }
        };

        parseCron();
    }, [expr]);

    const updateValue = (index, value) => {
        const updated = [...values];
        updated[index] = normalizeCronFieldValue(value, FIELD_INFO[index]);
        setValues(updated);
    };

    const copyText = async (text) => {
        if (!text) return;
        try {
             navigator.clipboard.writeText(text);
             setHasCopied(true);
             setTimeout(() => setHasCopied(false), 2000);
        } catch (error) {
            console.error(error);
        }
    };

    const loadExpression = (expression) => {

        const split = expression.trim().split(/\s+/);

        setValues([
            split[0] || "*",
            split[1] || "*",
            split[2] || "*",
            split[3] || "*",
            split[4] || "*",
        ]);
    };

    const referenceList = activeIndex === null
        ? ALL_REFERENCE
        : REFERENCE_BY_FIELD[activeIndex];

    const cronParts = useMemo(() => {

        const parts = cronExpression
            ? cronExpression.split(" ")
            : ["*", "*", "*", "*", "*"];

        return parts;

    }, [cronExpression]);

    return (
        <>
            <Col>

                {/* HEADER */}
                <ToolHeader title="Cron Parser & Generator" description="Generate and parse cron expressions easily" toolFav={toolFav} />

                {/* MODE BUTTONS */}
                <ToolModeToggle
                    options={[
                        { label: "Generator", value: "Generator", className: "encode" },
                        { label: "Parser", value: "Parser", className: "decode" }
                    ]}
                    activeValue={mode}
                    onChange={setMode}
                />

                <Row className="Formatter cron-tool-row">
                    <Col xs={12} className="cron-tool-column">

                        {mode === "Generator" ? (

                            <div className="cron-page">

                                <div className="cron-container">

                                    {/* GENERATOR CARD */}
                                    <div className="cron-card">

                                        <div className="expression-header">
                                            <label className="cron-title">
                                                Cron Expression Generator
                                            </label>
                                        </div>

                                        <div className="form-group">

                                            {/* FREQUENCY */}
                                            <div
                                                className="SQLDropdownWrapper"
                                                style={{
                                                    width: "272px",
                                                    marginBottom: "10px"
                                                }}
                                            >
                                                <label className="toolbar-label">
                                                    Frequency
                                                </label>

                                                <button
                                                    type="button"
                                                    className="SQLDropdownToggle"
                                                    style={{ width: "100%" }}
                                                    onClick={() =>
                                                        setShowFrequencyDropdown(prev => !prev)
                                                    }
                                                >
                                                    <span className="SQLDropdownText">
                                                        {frequency}
                                                        <CaretDownIcon />
                                                    </span>
                                                </button>

                                                {showFrequencyDropdown && (
                                                    <div className="SQLDropdownMenu">
                                                        {frequencyMap.map((value) => (
                                                            <button
                                                                key={value.frequency}
                                                                type="button"
                                                                className="SQLDropdownItem"
                                                                onClick={() => {
                                                                    setFrequency(value.frequency);
                                                                    setShowFrequencyDropdown(false);
                                                                }}
                                                            >
                                                                {value.frequency}
                                                            </button>

                                                        ))}

                                                    </div>
                                                )}
                                            </div>

                                            {/* MINUTE */}
                                            {frequency !== "Every minute" && (
                                                <div
                                                    className="SQLDropdownWrapper"
                                                    style={{
                                                        width: "272px",
                                                        marginBottom: "10px"
                                                    }}
                                                >
                                                    <label className="toolbar-label">
                                                        Minute (0-59)
                                                    </label>

                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="59"
                                                        value={cron.minute}
                                                        onChange={(e) => {
                                                            const raw = e.target.value;
                                                            const val = raw === "" ? "" : clampNumber(raw, 0, 59);
                                                            setCron(prev => ({ ...prev, minute: val }));
                                                        }}
                                                        className="SQLDropdownToggle"
                                                        style={{ width: "100%" }}
                                                    />
                                                </div>
                                            )}

                                            {/* HOUR */}
                                            {(frequency === "Daily" ||
                                                frequency === "Weekly" ||
                                                frequency === "Monthly") && (

                                                    <div
                                                        className="SQLDropdownWrapper"
                                                        style={{
                                                            width: "272px",
                                                            marginBottom: "10px"
                                                        }}
                                                    >
                                                        <label className="toolbar-label">
                                                            Hour (0-23)
                                                        </label>

                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="23"
                                                            value={cron.hour}
                                                            onChange={(e) => {
                                                                const raw = e.target.value;
                                                                const val = raw === "" ? "" : clampNumber(raw, 0, 23);
                                                                setCron(prev => ({ ...prev, hour: val }));
                                                            }}
                                                            className="SQLDropdownToggle"
                                                            style={{ width: "100%" }}
                                                        />
                                                    </div>
                                                )}

                                            {/* WEEKDAY */}
                                            {frequency === "Weekly" && (
                                                <div
                                                    className="SQLDropdownWrapper"
                                                    style={{
                                                        width: "272px",
                                                        marginBottom: "10px"
                                                    }}
                                                >
                                                    <label className="toolbar-label">
                                                        Day of Week (0-6)
                                                    </label>

                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="6"
                                                            value={cron.weekday}
                                                            onChange={(e) => {
                                                                const raw = e.target.value;
                                                                const val = raw === "" ? "" : clampNumber(raw, 0, 6);
                                                                setCron(prev => ({ ...prev, weekday: val }));
                                                            }}
                                                            className="SQLDropdownToggle"
                                                            style={{ width: "100%" }}
                                                        />
                                                </div>
                                            )}

                                            {/* DAY OF MONTH */}
                                            {frequency === "Monthly" && (
                                                <div
                                                    className="SQLDropdownWrapper"
                                                    style={{
                                                        width: "272px",
                                                        marginBottom: "10px"
                                                    }}
                                                >
                                                    <label className="toolbar-label">
                                                        Day of month (1-31)
                                                    </label>

                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max="31"
                                                            value={cron.day}
                                                            onChange={(e) => {
                                                                const raw = e.target.value;
                                                                const val = raw === "" ? "" : clampNumber(raw, 1, 31);
                                                                setCron(prev => ({ ...prev, day: val }));
                                                            }}
                                                            className="SQLDropdownToggle"
                                                            style={{ width: "100%" }}
                                                        />
                                                </div>
                                            )}

                                        </div>
                                    </div>

                                    {/* GENERATED EXPRESSION */}
                                    <div className="cron-card">

                                        <div className="expression-header">

                                            <label className="cron-title">
                                                Generated Expression
                                            </label>

                                            <button
                                                onClick={() => copyText(cronExpression)}
                                                className="copy-button"
                                            >
                                                <CopyIcon />
                                                {hasCopied? "Copied" : "Copy"}
                                            </button>

                                        </div>

                                        <div className="expression-box">
                                            {cronExpression}
                                        </div>

                                        {/* Meaning */}
                                        <p className="expression-meaning">
                                            Meaning :
                                            {(() => {
                                                switch (frequency) {
                                                    case "Every minute": return " Every minute";
                                                    case "Hourly": return ` Every hour at minute ${cron.minute}`;
                                                    case "Daily": return ` Every day at ${String(cron.hour).padStart(2, '0')}:${String(cron.minute).padStart(2, '0')}`;
                                                    case "Weekly": return ` Every week on day ${cron.weekday} at ${String(cron.hour).padStart(2, '0')}:${String(cron.minute).padStart(2, '0')}`;
                                                    case "Monthly": return ` Every month on day ${cron.day} at ${String(cron.hour).padStart(2, '0')}:${String(cron.minute).padStart(2, '0')}`;
                                                    default: return "";
                                                }
                                            })()} </p>
                                        {/* CRON PARTS */}
                                        <div className="cron-parts">

                                            {[
                                                { value: cronParts[0], label: "minute" },
                                                { value: cronParts[1], label: "hour" },
                                                { value: cronParts[2], label: "day of month" },
                                                { value: cronParts[3], label: "month" },
                                                { value: cronParts[4], label: "day of week" },
                                            ].map((item) => (

                                                <div
                                                    key={item.label}
                                                    className="cron-part-card"
                                                >
                                                    <div className="cron-part-value">
                                                        {item.value}
                                                    </div>

                                                    <div className="cron-part-label">
                                                        {item.label}
                                                    </div>
                                                </div>

                                            ))}

                                        </div>
                                    </div>

                                </div>
                            </div>

                        ) : (

                            <div className="cron-page">

                                <div className="cron-container">

                                    {/* PARSER */}
                                    <div className="cron-card">

                                        <div className="expression-header">
                                            <label className="cron-title">
                                                Cron Expression Parser
                                            </label>
                                        </div>

                                        <div className="cron-wrapper">
                                            {/* TOP INPUT */}
                                            <div className="cron-top-row">
                                                {FIELD_INFO.map((field, index) => (
                                                    <input
                                                        type="number"
                                                        key={index}
                                                        ref={(el) =>
                                                            inputRefs.current[index] = el
                                                        }
                                                        value={values[index]}
                                                        onChange={(e) =>
                                                            updateValue(index, e.target.value)
                                                        }
                                                        onFocus={() =>
                                                            setActiveIndex(index)
                                                        }
                                                        min={field.min}
                                                        max={field.max}
                                                        className={`cron-input ${activeIndex === index ? "active" : ""}`}
                                                    />

                                                ))}

                                            </div>
                                            {/* <div className="cron-top-row" style={{ gap: "12px", marginTop: "12px" }}>
                                                <div className="SQLDropdownWrapper" style={{ width: "272px" }}>
                                                    <label className="toolbar-label">Timezone</label>
                                                    <input
                                                        type="text"
                                                        value={parserTimezone}
                                                        onChange={(e) => setParserTimezone(e.target.value)}
                                                        className="SQLDropdownToggle"
                                                        style={{ width: "100%" }}
                                                        placeholder="UTC"
                                                    />
                                                </div>
                                                <div className="SQLDropdownWrapper" style={{ width: "272px" }}>
                                                    <label className="toolbar-label">Occurrences</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={parserOccurrences}
                                                        onChange={(e) => setParserOccurrences(Number(e.target.value) || 1)}
                                                        className="SQLDropdownToggle"
                                                        style={{ width: "100%" }}
                                                    />
                                                </div>
                                            </div> */}

                                            {/* FIELD CARDS */}
                                            <div className="cron-bottom-row">
                                                {FIELD_INFO.map((field, index) => (
                                                    <div
                                                        key={index}
                                                        className={`cron-info-card ${activeIndex === index ? "active" : ""}`}
                                                        onClick={() =>
                                                            setActiveIndex(index)
                                                        }
                                                    >
                                                        <div className="cron-value">
                                                            {values[index]}
                                                        </div>

                                                        <div className="cron-label">
                                                            {field.label}
                                                        </div>

                                                        <div className="cron-range">
                                                            {field.range}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    {/* DESCRIPTION */}
                                    <div className="cron-card">

                                        <div className="expression-header">

                                            <label className="cron-title">
                                                Description
                                            </label>

                                            <button
                                                type="button"
                                                className="copy-button"
                                                onClick={() => copyText(parserCronExpression)}
                                            >
                                                <CopyIcon />
                                                {hasCopied ? "Copied" : "Copy"}
                                            </button>

                                        </div>

                                        <div className="expression-box">
                                            {parserCronExpression}
                                        </div>

                                        <div
                                            className={`cron-description ${parserResponse.valid ? "valid" : "invalid"}`}
                                        >
                                            <p>
                                                {parserResponse.description}
                                            </p>
                                        </div>

                                        {/* <div className="cron-parser-meta">
                                            <div className="meta-row">
                                                <span>Timezone:</span>
                                                <strong>{parserResponse.timezone || parserTimezone}</strong>
                                            </div>
                                            <div className="meta-row">
                                                <span>Processing time:</span>
                                                <strong>{parserResponse.processingTimeMs} ms</strong>
                                            </div>
                                            {parserResponse.nextExecutions?.length > 0 && (
                                                <div className="next-executions">
                                                    <div className="meta-row">
                                                        <span>Next executions:</span>
                                                    </div>
                                                    <ul>
                                                        {parserResponse.nextExecutions.map((item, idx) => (
                                                            <li key={idx}>{item}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div> */}
                                    </div>

                                   
                                </div>
                                 {/* REFERENCE */}
                                    <div className="cron-card" style={{ marginTop: "20px" }}>
                                        <div className="expression-header">
                                            <label className="cron-title">
                                                {activeIndex === null
                                                    ? "Reference Patterns"
                                                    : `Patterns for ${FIELD_INFO[activeIndex]?.label}`
                                                }
                                            </label>

                                        </div>

                                        <div className="reference-list">
                                            {referenceList.map(([expression, desc]) => (
                                                <button
                                                    key={expression}
                                                    className="reference-item"
                                                    onClick={() =>
                                                        loadExpression(expression)
                                                    }
                                                >
                                                    <span className="reference-expression">
                                                        {expression}
                                                    </span>
                                                    <span className="reference-desc">
                                                        {desc}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                            </div>
                        )}
                    </Col>
                </Row >
            </Col >
        </>
    );
}
function buildCron(freq, p) {

    switch (freq) {

        case "Every minute":
            return "* * * * *";

        case "Hourly":
            return `${p.minute} * * * *`;

        case "Daily":
            return `${p.minute} ${p.hour} * * *`;

        case "Weekly":
            return `${p.minute} ${p.hour} * * ${p.weekday}`;

        case "Monthly":
            return `${p.minute} ${p.hour} ${p.day} * *`;

        default:
            return "* * * * *";
    }
}

export default CronGeneratorParser;
